import { z } from "zod";

// ADV-1115: the unified row shape for the three-source entity activity feed.
// Communications, media submissions, and allowlisted activity_events are each
// normalized into this envelope (discriminated by `source`) at the service
// layer, then merge-sorted by `occurred_at` descending.

export const entityActivitySources = [
  "communication",
  "media",
  "activity",
] as const;
export type EntityActivitySource = (typeof entityActivitySources)[number];

// A normalized attachment thumbnail, unified across comm_attachments and
// media_items. A strict subset of both source shapes so the Phase-2
// normalizers compile without casts.
export const EntityActivityAttachmentSchema = z
  .object({
    url: z.string().url().describe("URL"),
    mime_type: z.string().nullable().describe("MIME Type"),
    caption: z.string().nullable().optional().describe("Caption"),
    width_px: z.number().int().nullable().optional().describe("Width Px"),
    height_px: z.number().int().nullable().optional().describe("Height Px"),
  })
  .describe("Attachment");
export type EntityActivityAttachment = z.infer<
  typeof EntityActivityAttachmentSchema
>;

export const EntityActivityRowSchema = z
  .object({
    source: z.enum(entityActivitySources).describe("Source"),
    id: z.string().uuid().describe("ID"),
    entity_type: z.string().nullable().describe("Entity Type"),
    entity_id: z.string().uuid().nullable().describe("Entity"),
    // offset:true accepts the timezone-offset form PostgREST serializes
    // timestamptz into (e.g. 2026-05-24T10:00:00+00:00) — the three upstream
    // sources all type their timestamps as plain z.string(), so the strict
    // default (which only accepts a trailing Z) would reject real feed data.
    occurred_at: z.string().datetime({ offset: true }).describe("Occurred At"),
    // Comm channel / activity kind — discriminator detail for the row renderer.
    channel: z.string().nullable().optional().describe("Channel"),
    direction: z.string().nullable().optional().describe("Direction"),
    kind: z.string().nullable().optional().describe("Kind"),
    label: z.string().describe("Label"),
    body: z.string().nullable().describe("Body"),
    actor_type: z.string().nullable().describe("Actor Type"),
    actor_id: z.string().nullable().describe("Actor"),
    // ADV-1344: actor identity + per-row source link, resolved DB-first in the
    // feed views (core.activity_events_feed_v / communications_feed_v /
    // media_submissions_feed_v) — never via a client-side join. All nullable so
    // system/api_key actors, link-less rows, and pre-existing API responses
    // degrade gracefully (→ monogram / no link).
    actor_name: z.string().nullable().optional().describe("Actor Name"),
    actor_avatar_url: z
      .string()
      .url()
      .nullable()
      .optional()
      .describe("Actor Avatar"),
    // The deep-link target for the row. For comms/activity this equals the host
    // entity; for media rows it is the matched media_subjects link (the child
    // job for an opportunity rollup), which is why it is a separate field from
    // entity_type/entity_id rather than overloaded onto them.
    source_entity_type: z
      .string()
      .nullable()
      .optional()
      .describe("Source Entity Type"),
    source_entity_id: z
      .string()
      .uuid()
      .nullable()
      .optional()
      .describe("Source Entity"),
    attachment_count: z.number().int().describe("Attachment Count"),
    attachments: z
      .array(EntityActivityAttachmentSchema)
      .optional()
      .describe("Attachments"),
    metadata: z.record(z.unknown()).describe("Metadata"),
  })
  .describe("Entity Activity Row");
export type EntityActivityRow = z.infer<typeof EntityActivityRowSchema>;
